import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import PageHeader from "components/Common/PageHeader";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import CustomButton from "components/Common/ui/CustomButton";

import AddEditExpenseModal from "../modals/AddEditExpenseModal";
import ExpensesTable from "../table/ExpensesTable";

import useExpense from "../hooks/useExpense";
import useSuppliers from "../../settings/hooks/useSuppliers";
import { FilterField } from "components/Common/TableFilter/TableFilter";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";
import useReference from "hooks/useReference";

const Expenses = () => {
  const { t } = useTranslation();
  const ability = useAbility();

  const {
    expenses,
    infoPagination,
    loading,
    selectedExpense,
    setSelectedExpense,
    isModalOpen,
    setIsModalOpen,
    fetchExpenses,
    handleCreate,
    handleUpdate,
    handleDelete,
  } = useExpense();

  const { fetchPaymentMethods, paymentMethods , loadingPaymentMethods } = useReference();

  const { fetchSuppliers, suppliers } = useSuppliers();

  const breadcrumbItems = [
    { label: "finance_dashboard", path: all_routes.financeDashboard },
    { label: t("expenses"), active: true },
  ];

  const [filters, setFilters] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    fetchExpenses({ page: 1, limit: 10 });
    fetchSuppliers();
    fetchPaymentMethods();
  }, [fetchExpenses, fetchSuppliers, fetchPaymentMethods]);


  const filterFields: FilterField[] = [
    { key: "search", label: t("search"), type: "text" },
    {
      key: "supplierId",
      label: t("supplier"),
      type: "select",
      options: suppliers.map((supplier) => ({
        label: supplier.name,
        value: supplier.id,
      })),
    },
    {
      key: "dueDate",
      label: t("due_date"),
      type: "range",
      rangeKeys: { start: "startDate", end: "endDate" },
    },
    {
      key: "status",
      label: t("status"),
      type: "select",
      options: [
        { label: t("expenses_status.coming"), value: "coming" },
        { label: t("expenses_status.overdue"), value: "overdue" },
        { label: t("expenses_status.approved"), value: "approved" },
        { label: t("expenses_status.rejected"), value: "rejected" },
      ],
    },
  ];


  const handleSaveExpense = async (expenseData: any) => {
    let res = false;

    if (selectedExpense) {
      res = await handleUpdate(selectedExpense.id, expenseData);
    } else {
      res = await handleCreate(expenseData);
    }

    if (res) {
      setIsModalOpen(false);
      setSelectedExpense(null);
      fetchExpenses({ page: 1, limit: 10, ...filters });
    }
  };

  const handleEdit = (record: any) => {
    setSelectedExpense(record);
    setIsModalOpen(true);
  };

  const handleFilter = (newFilters: any) => {
    const params = {
      ...newFilters,
      page: 1,
      limit: 10,
    };

    setFilters(newFilters);
    fetchExpenses(params);
  };


  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("expenses")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "Expense") && (
            <CustomButton
              onClick={() => {
                setSelectedExpense(null);
                setIsModalOpen(true);
              }}
            >
              <i className="ti ti-plus me-2" />
              {t("add_expense")}
            </CustomButton>
          )}

          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilter}
            onReset={() => {
              setFilters({});
              fetchExpenses({ page: 1, limit: 10 });
            }}
          />
        </div>

        <ExpensesTable
          dataSource={expenses ?? []}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          dataPaginate={{
            current: infoPagination?.page || 1,
            pageSize: infoPagination?.limit || 10,
            total: infoPagination?.total,
          }}
          onChange={(pg) => {
            const params = {
              ...filters,
              page: pg.current || 1,
              limit: pg.pageSize || 10,
            };

            fetchExpenses(params);
          }}
        />

        <AddEditExpenseModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          
          expenseData={selectedExpense}
          onSave={handleSaveExpense}
          suppliers={suppliers}
          paymentMethods={paymentMethods}
        />
      </div>
    </div>
  );
};

export default Expenses;
